import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Dropzone from "react-dropzone";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
//import axios from "axios";
import apiInstance from "../../api/api";
import { API_URL, MEDIA_URL } from "../../configs";
import authHeader from "../../helpers/header";

function toMediaUrl(path) {
  // Remove the "storage/images/" prefix if present
  if (path.startsWith("storage/images/")) {
    return `media/${path.replace("storage/images/", "")}`;
  }
  // fallback: return as is
  return path;
}

const TiptapEditor = ({ value, onChange }) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({ openOnClick: true }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange && onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  const handleImageUpload = async (files) => {
    setUploadError("");
    setUploading(true);
    setSelectedFile(files[0]);
    const formData = new FormData();
    formData.append("data", files[0]);
    formData.append("type", "content");
    try {
      const res = await apiInstance.post(`${API_URL}files/upload_image`, formData, {
        headers: {
          ...authHeader(),
          "Content-Type": "multipart/form-data"
        },
        withCredentials: true
      });
      if (res.data && res.data.detail && res.data.detail.original && res.data.detail.original.path) {
        setUploadedUrl(res.data.detail.original.path);
      } else {
        setUploadError("Upload failed: No URL returned");
      }
    } catch (err) {
      setUploadError("Upload failed");
    }
    setUploading(false);
  };

  const handleInsertImage = () => {
    if (uploadedUrl && editor) {
      editor.chain().focus().setImage({ src: `${MEDIA_URL}${toMediaUrl(uploadedUrl)}` }).run();
      setShowImageModal(false);
      setUploadedUrl("");
      setSelectedFile(null);
      setUploadError("");
    }
  };

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <div
      className={`border rounded p-2${fullscreen ? ' position-fixed top-0 start-0 w-100 h-100 bg-white' : ''}`}
      style={fullscreen
        ? {
            minHeight: '100vh',
            left: 0,
            overflow: 'auto',
            padding: 32,
            zIndex: 9999,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)',
          }
        : { minHeight: 300 }}
    >
      <style>{`
        .tiptap.ProseMirror-focused {
          outline: none !important;
        }
        .tiptap img {
          max-width: 100%;
          height: auto;
          display: block;
        }
      `}</style>
      <div className="mb-2 d-flex flex-wrap gap-1 align-items-center justify-content-between">
        <div className="d-flex flex-wrap gap-1">
          <button type="button" className={editor.isActive('bold') ? 'btn btn-sm btn-dark' : 'btn btn-sm btn-light'} onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></button>
          <button type="button" className={editor.isActive('italic') ? 'btn btn-sm btn-dark' : 'btn btn-sm btn-light'} onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></button>
          <button type="button" className={editor.isActive('strike') ? 'btn btn-sm btn-dark' : 'btn btn-sm btn-light'} onClick={() => editor.chain().focus().toggleStrike().run()}><s>S</s></button>
          <button type="button" className={editor.isActive('heading', { level: 1 }) ? 'btn btn-sm btn-dark' : 'btn btn-sm btn-light'} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>H1</button>
          <button type="button" className={editor.isActive('heading', { level: 2 }) ? 'btn btn-sm btn-dark' : 'btn btn-sm btn-light'} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>H2</button>
          <button type="button" className={editor.isActive('bulletList') ? 'btn btn-sm btn-dark' : 'btn btn-sm btn-light'} onClick={() => editor.chain().focus().toggleBulletList().run()}>&#8226; List</button>
          <button type="button" className={editor.isActive('orderedList') ? 'btn btn-sm btn-dark' : 'btn btn-sm btn-light'} onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</button>
          <button type="button" className={editor.isActive('blockquote') ? 'btn btn-sm btn-dark' : 'btn btn-sm btn-light'} onClick={() => editor.chain().focus().toggleBlockquote().run()}>" Blockquote</button>
          <button type="button" className={editor.isActive('link') ? 'btn btn-sm btn-dark' : 'btn btn-sm btn-light'} onClick={() => { const url = window.prompt('Enter URL'); if (url) { editor.chain().focus().setLink({ href: url }).run(); } }}>🔗 Link</button>
          <button type="button" className={'btn btn-sm btn-light'} onClick={() => setShowImageModal(true)}>🖼️ Image</button>
          <button type="button" className={'btn btn-sm btn-light'} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>Clear</button>
          <button type="button" className={fullscreen ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-light'} onClick={() => setFullscreen((prev) => !prev)}>{fullscreen ? 'Exit Fullscreen' : <i className="bi bi-arrows-fullscreen"></i>}</button>
        </div>
        
      </div>
      <div style={{ minHeight: 300 }}>
        <EditorContent editor={editor} className="tiptap" />
      </div>
      {/* Image Upload Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-4">Upload Image</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Dropzone
            onDrop={handleImageUpload}
            accept={{ "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"] }}
            multiple={false}
          >
            {({ getRootProps, getInputProps }) => (
              <section className="bg-light text-center p-4 border rounded">
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <span className="text-muted small">
                    Drag 'n' drop an image here, or click to select
                  </span>
                </div>
              </section>
            )}
          </Dropzone>
          {uploading && <div className="text-center my-2">Uploading...</div>}
          {uploadError && <div className="alert alert-danger py-2 mt-2">{uploadError}</div>}
          {uploadedUrl && (
            <div className="text-center my-2">
              <img src={`${MEDIA_URL}${toMediaUrl(uploadedUrl)}`} alt="Uploaded preview" style={{ maxWidth: 200, maxHeight: 200 }} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleInsertImage} disabled={!uploadedUrl}>
            Insert
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TiptapEditor;
